import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { api } from '../../utils/api';
import { useHasFeature } from '../../store/featureFlagsStore';
import { useGetPerformanceReviewRounds } from '../../api/generated/performance-review/performance-review';
import { Eyebrow, NumberMono } from '../performance-review/parts/primitives';
import { useDevice } from '../../hooks/useDevice';

type Decision = 'promote' | 'confirm' | 'no-change' | 'demote' | 'none';
type DecisionCounts = Record<Decision, number>;

interface ScoreGroup {
  participants: number;
  overall: number | null;
  // Изменение — только по тем, у кого оценка есть и сейчас, и в прошлом ревью.
  comparedOverall: number | null;
  previousOverall: number | null;
  compared: number;
  blocks: Record<string, number>;
  decisions: DecisionCounts;
}
interface TeamRow extends ScoreGroup {
  teamId: string;
  team: string;
  department: { _id: string; name: string } | null;
}
interface RoundAnalytics {
  round: { _id: string; title: string; stage: string };
  previousRound: { _id: string; title: string } | null;
  blocks: { questionId: string; block: string }[];
  total: ScoreGroup;
  teams: TeamRow[];
}
interface RoundLite {
  _id: string;
  title: string;
  stage: string;
  endDate: string;
}

const OVERALL = 'general-overall-score';
// Как DELTA_DEAD_ZONE на бэкенде: меньше — шум четырёх ревьюеров, а не движение.
const DEAD_ZONE = 0.3;
const DECISIONS: { key: Decision; color: string }[] = [
  { key: 'promote', color: 'var(--mantine-color-teal-6)' },
  { key: 'confirm', color: 'var(--mantine-color-blue-5)' },
  { key: 'no-change', color: 'var(--mantine-color-gray-5)' },
  { key: 'demote', color: 'var(--mantine-color-red-6)' },
  { key: 'none', color: 'var(--mantine-color-default-border)' },
];

const useDecisionLabel = () => {
  const { t } = useTranslation();
  return (d: Decision) =>
    d === 'none' ? t('perfReview.outcome.decisionPlaceholder') : t(`perfReview.outcome.${d}`);
};

const useScore = () => {
  const { i18n } = useTranslation();
  return (n: number | null | undefined) =>
    n == null
      ? '—'
      : n.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

/** Сдвиг к прошлому раунду. Внутри мёртвой зоны — серым, без знака «рост/падение». */
const Delta: React.FC<{ now: number | null; before: number | null; size?: 'xs' | 'sm' }> = ({
  now,
  before,
  size = 'xs',
}) => {
  const score = useScore();
  if (now == null || before == null) return null;
  const d = now - before;
  const isMove = Math.abs(d) >= DEAD_ZONE;
  return (
    <Text
      component="span"
      size={size}
      fw={600}
      c={!isMove ? 'dimmed' : d > 0 ? 'teal' : 'red'}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {d > 0 ? '+' : d < 0 ? '−' : '±'}
      {score(Math.abs(d))}
    </Text>
  );
};

/** Решения по грейдам одной полосой: доля каждого решения среди участников. */
const DecisionBar: React.FC<{ counts: DecisionCounts; height?: number }> = ({
  counts,
  height = 8,
}) => {
  const label = useDecisionLabel();
  const total = DECISIONS.reduce((s, d) => s + counts[d.key], 0);
  if (!total) return null;
  return (
    <Group gap={2} wrap="nowrap" style={{ height, borderRadius: 999, overflow: 'hidden' }}>
      {DECISIONS.filter((d) => counts[d.key]).map((d) => (
        <Tooltip key={d.key} label={`${label(d.key)}: ${counts[d.key]}`} withArrow>
          <Box style={{ flex: counts[d.key], height: '100%', background: d.color }} />
        </Tooltip>
      ))}
    </Group>
  );
};

const DecisionLegend: React.FC<{ counts: DecisionCounts }> = ({ counts }) => {
  const label = useDecisionLabel();
  return (
    <Group gap="md" mt="xs">
      {DECISIONS.filter((d) => counts[d.key]).map((d) => (
        <Group key={d.key} gap={6} wrap="nowrap">
          <Box style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
          <Text size="xs" c="dimmed">
            {label(d.key)}
          </Text>
          <NumberMono style={{ fontSize: 12, fontWeight: 600 }}>{counts[d.key]}</NumberMono>
        </Group>
      ))}
    </Group>
  );
};

export const Analytics: React.FC = () => {
  const { t } = useTranslation();
  const isOn = useHasFeature('reviewAnalytics');
  const isPhone = useDevice() === 'mobile';
  const score = useScore();
  const { data: allRounds, isLoading: isRoundsLoading } = useGetPerformanceReviewRounds({
    query: { enabled: isOn },
  });
  // Снимки оценок появляются на саммари: раньше считать нечего.
  const rounds = useMemo(
    () =>
      ((allRounds as unknown as RoundLite[]) ?? [])
        .filter((r) => r.stage === 'summary' || r.stage === 'finished')
        .sort((a, b) => b.endDate.localeCompare(a.endDate)),
    [allRounds],
  );
  const [picked, setPicked] = useState<string | null>(null);
  const roundId = picked ?? rounds[0]?._id ?? null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['perf-review-analytics', roundId],
    queryFn: (): Promise<RoundAnalytics> => api(`/performance-review/rounds/${roundId}/analytics`),
    enabled: isOn && !!roundId,
    retry: false,
  });

  if (!isOn) return <Navigate to="/dashboard" replace />;

  const blocks = (data?.blocks ?? []).filter((b) => b.questionId !== OVERALL);
  const byDepartment = new Map<string, { name: string | null; rows: TeamRow[] }>();
  for (const row of data?.teams ?? []) {
    const key = row.department?._id ?? '';
    if (!byDepartment.has(key))
      byDepartment.set(key, { name: row.department?.name ?? null, rows: [] });
    byDepartment.get(key)!.rows.push(row);
  }
  // Команды без отдела — в конце.
  const departments = Array.from(byDepartment.values()).sort((a, b) =>
    a.name == null ? 1 : b.name == null ? -1 : a.name.localeCompare(b.name),
  );

  return (
    <Stack gap="lg" p={{ base: 'md', sm: 'xl' }} maw={1200}>
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
        <Box>
          <Group gap="xs" align="center">
            <Title order={2}>{t('analytics.title')}</Title>
            <Badge size="xs" variant="outline">
              beta
            </Badge>
          </Group>
          <Text c="dimmed" size="sm" mt={4}>
            {t('analytics.subtitle')}
          </Text>
        </Box>
        {rounds.length > 0 && (
          <Select
            aria-label={t('analytics.round')}
            w={{ base: '100%', xs: 260 }}
            data={rounds.map((r) => ({ value: r._id, label: r.title }))}
            value={roundId}
            onChange={setPicked}
            allowDeselect={false}
          />
        )}
      </Group>

      {isRoundsLoading || isLoading ? (
        <Loader size="sm" />
      ) : rounds.length === 0 ? (
        <Text c="dimmed">{t('analytics.noRounds')}</Text>
      ) : isError || !data ? (
        <Text c="dimmed">{t('analytics.loadFailed')}</Text>
      ) : (
        <>
          <Paper withBorder radius="lg" p={{ base: 'md', sm: 'lg' }}>
            <Group align="flex-start" gap="xl" wrap="wrap">
              <Box miw={160}>
                <Eyebrow>{t('analytics.overall')}</Eyebrow>
                <Group gap={10} align="baseline" mt={6} wrap="nowrap">
                  <NumberMono style={{ fontSize: 40, fontWeight: 700, lineHeight: 1 }}>
                    {score(data.total.overall)}
                  </NumberMono>
                  <Text size="sm" c="dimmed">
                    / 5
                  </Text>
                  <Delta
                    now={data.total.comparedOverall}
                    before={data.total.previousOverall}
                    size="sm"
                  />
                </Group>
                <Text size="xs" c="dimmed" mt={6}>
                  {t('analytics.participants', { count: data.total.participants })}
                  {data.total.compared > 0 &&
                    ` · ${
                      data.previousRound
                        ? t('analytics.comparedTo', {
                            round: data.previousRound.title,
                            count: data.total.compared,
                          })
                        : t('analytics.comparedToEach', { count: data.total.compared })
                    }`}
                </Text>
              </Box>
              <Box style={{ flex: '1 1 280px' }}>
                <Eyebrow>{t('analytics.decisions')}</Eyebrow>
                <Box mt={12}>
                  <DecisionBar counts={data.total.decisions} height={12} />
                  <DecisionLegend counts={data.total.decisions} />
                </Box>
              </Box>
            </Group>
          </Paper>

          {data.teams.length === 0 ? (
            <Text c="dimmed">{t('analytics.noTeams')}</Text>
          ) : isPhone ? (
            // На телефоне таблица уезжает вбок и прячет главное число: команды списком,
            // без колонок по блокам.
            <Stack gap="lg">
              {departments.map((dep) => (
                <Stack key={dep.name ?? ''} gap="xs">
                  <Eyebrow>{dep.name ?? t('analytics.noDepartment')}</Eyebrow>
                  {dep.rows.map((row) => (
                    <Paper key={row.teamId} withBorder radius="md" p="sm">
                      <Group justify="space-between" wrap="nowrap" align="flex-start">
                        <Box miw={0}>
                          <Text fw={500}>{row.team}</Text>
                          <Text size="xs" c="dimmed">
                            {t('analytics.participants', { count: row.participants })}
                          </Text>
                        </Box>
                        <Group gap={8} wrap="nowrap">
                          <Delta now={row.comparedOverall} before={row.previousOverall} />
                          <NumberMono style={{ fontSize: 20, fontWeight: 700 }}>
                            {score(row.overall)}
                          </NumberMono>
                        </Group>
                      </Group>
                      <Box mt="sm">
                        <DecisionBar counts={row.decisions} />
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Paper withBorder radius="lg">
              <Table.ScrollContainer minWidth={560 + blocks.length * 110}>
                <Table verticalSpacing="sm" horizontalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('analytics.team')}</Table.Th>
                      <Table.Th ta="right">{t('analytics.people')}</Table.Th>
                      <Table.Th ta="right">{t('analytics.overallShort')}</Table.Th>
                      {blocks.map((b) => (
                        <Table.Th key={b.questionId} ta="right" fw={500} c="dimmed">
                          {b.block}
                        </Table.Th>
                      ))}
                      <Table.Th miw={160}>{t('analytics.decisions')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {departments.map((dep) => (
                      <React.Fragment key={dep.name ?? ''}>
                        <Table.Tr>
                          <Table.Td colSpan={4 + blocks.length} pt="md" pb={4}>
                            <Eyebrow>{dep.name ?? t('analytics.noDepartment')}</Eyebrow>
                          </Table.Td>
                        </Table.Tr>
                        {dep.rows.map((row) => (
                          <Table.Tr key={row.teamId}>
                            <Table.Td fw={500}>{row.team}</Table.Td>
                            <Table.Td ta="right">
                              <NumberMono>{row.participants}</NumberMono>
                            </Table.Td>
                            <Table.Td ta="right">
                              <Group gap={8} justify="flex-end" wrap="nowrap">
                                <Delta now={row.comparedOverall} before={row.previousOverall} />
                                <NumberMono style={{ fontWeight: 700 }}>
                                  {score(row.overall)}
                                </NumberMono>
                              </Group>
                            </Table.Td>
                            {blocks.map((b) => (
                              <Table.Td key={b.questionId} ta="right">
                                <NumberMono>{score(row.blocks[b.questionId])}</NumberMono>
                              </Table.Td>
                            ))}
                            <Table.Td>
                              <DecisionBar counts={row.decisions} />
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
};
